/**
 * Industry (vertical) intent — remembered across the visit.
 *
 * Only two CTAs on the whole site carried `?vertical=` (the two inside
 * IndustryDetailsPage) plus one on ComparisonPage. Everything else — the header
 * "Get Started" button, the pricing CTAs, the hero, the blog sidebar — sends the
 * visitor to a bare /signup. So somebody who read /industry/hotel end to end and
 * then clicked the button in the header landed on the signup form with
 * "General Business" preselected.
 *
 * This records the last industry page a visitor looked at, so the signup form
 * can preselect it regardless of which CTA finally gets clicked. LAST touch
 * wins here, deliberately unlike the referral cookie's first-touch rule: the
 * most recent industry someone read is the best guess at the business they run.
 *
 * SLUG_TO_VERTICAL is the URL-slug → mothership vertical-value map. Every
 * right-hand value MUST exist in INDUSTRY_OPTIONS in SignupPage.tsx (which in
 * turn must be a subset of mothership VERTICAL_MODULES in
 * c:/wamp64/www/indevithub.com/api/app/core/verticals.py). An orphan value here
 * is a dead URL slug: it silently falls back to General Business.
 */
import { getCookie, setCookie } from "../components/ReferralTracker/ReferralTracker";
import { attributionAllowedNow } from "../context/ConsentContext";

const VERTICAL_COOKIE_NAME = "axoix_vertical";
const VERTICAL_COOKIE_DAYS = 30;

// Last industry viewed during this visit (in memory — see rememberVerticalIntent).
let sessionVertical: string | null = null;

// Left side: the slug as it appears in /industry/:slug and /demo/:vertical.
// Right side: the value stored in tenants.settings.primary_vertical.
export const SLUG_TO_VERTICAL: Record<string, string> = {
  "hotel": "hotel",
  "restaurant": "restaurant",
  "travel": "travel",
  "healthcare": "healthcare",
  "education": "education",
  "logistics": "logistics",
  "real-estate": "realestate",
  "retail": "retail",
  // Added 2026-08-20 alongside the /industry/ecommerce page. Without this the
  // page's own CTA (?vertical=ecommerce) resolved to null and the signup form
  // fell back to its default industry — the visitor's stated intent was lost
  // at the last step.
  "ecommerce": "ecommerce",
  "e-commerce": "ecommerce",
  "manufacturing": "manufacturing",
  "construction": "construction",
  "govt-contractor": "gov_contractor",
  "gov-contractor": "gov_contractor",
  "professional-services": "services",
  "it-services": "it_services",
  "consulting": "consulting",
  "trading": "trading",
  "car-dealer": "car_dealer",
  "bike-dealer": "bike_dealer",
  "salon-spa": "salon",
  "gym-fitness": "gym",
  "arena-gaming": "arena",
  "study-abroad": "study_abroad",
  // Pseudo-vertical: /demo/pg runs the hotel vertical in PG mode. There is no
  // `pg` key in VERTICAL_MODULES, so it must resolve to hotel.
  "pg": "hotel",
};

/**
 * Slug (or an already-resolved vertical value) → vertical value.
 * Returns null when the input maps to nothing we can store.
 */
export function resolveVertical(slugOrValue: string | undefined | null): string | null {
  if (!slugOrValue) return null;
  const key = slugOrValue.trim().toLowerCase();
  // hasOwnProperty, not a truthy index: /demo/constructor is a reachable URL and
  // a bare lookup would hand back Object's constructor as if it were a vertical.
  if (Object.prototype.hasOwnProperty.call(SLUG_TO_VERTICAL, key)) return SLUG_TO_VERTICAL[key];
  // Already a vertical value (e.g. "realestate" arriving as ?vertical=realestate)
  if (Object.values(SLUG_TO_VERTICAL).includes(key)) return key;
  return null;
}

/**
 * Called by every page that is *about* one industry. Takes either the URL slug
 * or the vertical value — the page can pass whichever it already holds.
 */
export function rememberVerticalIntent(slugOrValue: string | undefined | null): void {
  const vertical = resolveVertical(slugOrValue);
  if (!vertical) return;
  // Memory always — it preselects the signup form within this visit and
  // stores nothing on the device. The cookie, which carries it to a later
  // visit, follows the attribution rule (EU/EEA/UK: Marketing consent only).
  // Not resolved yet: ReferralTracker calls persistVerticalIntent() once it is.
  sessionVertical = vertical;
  if (attributionAllowedNow() === true) {
    setCookie(VERTICAL_COOKIE_NAME, vertical, VERTICAL_COOKIE_DAYS);
  }
}

/** Write this visit's intent to the cookie — called once storage is allowed. */
export function persistVerticalIntent(): void {
  if (sessionVertical) setCookie(VERTICAL_COOKIE_NAME, sessionVertical, VERTICAL_COOKIE_DAYS);
}

/** Drop the cookie only (consent withdrawn); this visit's memory stays. */
export function forgetStoredVerticalIntent(): void {
  document.cookie = `${VERTICAL_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

/** The last industry the visitor showed interest in, or null. */
export function getVerticalIntent(): string | null {
  return sessionVertical ?? resolveVertical(getCookie(VERTICAL_COOKIE_NAME));
}

/** Call after a tenant is actually created — the intent has been spent. */
export function clearVerticalIntent(): void {
  sessionVertical = null;
  forgetStoredVerticalIntent();
}
